import { ProductAdditionalInfoComponent } from '../product-additional-info/product-additional-info.component';
import { ProductFlowDataCollection } from '../product-flow/product-flow.types';
import { AsyncPipe, NgClass } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { RootReducer, Store } from '@app/app.reducers';
import type { UploadMaterialData } from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { StorePersistService } from '@app/checkout/modules/checkout-shared/services/store-persist.service';
import {
	getStepData,
	ProductFlowPage,
	setStepStateValid,
} from '@app/product/reducers/product-flow.reducer';
import { ProductEditorService } from '@app/product/services/product-editor.service';
import { PrintfileReportComponent } from '@app/shared/components/printfile-report/printfile-report.component';
import { FileUploadStatus, UploadNotification } from '@app/shared/reducers/upload.reducer';
import { I18nService } from '@app/shared/utils/i18n.service';
import { getDebug } from '@app/shared/utils/util';
import type { FileItem as FileItemLegacy } from '@app/upload/upload-legacy/upload.component';
import type { FileItem as FileItemV2 } from '@app/upload/upload-v2/upload.component';
import { FileItem } from '@app/upload/upload.component';
import { DynamicUploadModule } from '@app/upload/upload.module';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/** Upload material component for the product flow. */
@UntilDestroy()
@Component({
	standalone: true,
	selector: 'g-upload-material',
	templateUrl: './upload-material.component.html',
	styleUrls: ['./upload-material.component.scss'],
	imports: [
		ProductAdditionalInfoComponent,
		PrintfileReportComponent,
		FlexLayoutModule,
		DynamicUploadModule,
		AsyncPipe,
		NgClass,
	],
})
export class UploadMaterialComponent implements OnInit, ProductFlowDataCollection {
	@Input() product?: api.ProductDto;
	private readonly debug = getDebug('UploadMaterialComponent');
	notification?: UploadNotification;
	hasCartItem = false;
	isFiletypeError = false;
	currentFileItem?: FileItem;
	uploadOptions$ = this.productEditor.uploadOptions$;
	item?: api.CartItemWithProductDto;
	readonly notificationType = UploadNotification; // For template.
	readonly instructionsLink = this.i18n.getCustomFeatureLocale('show_material_instructions_link')
		? this.i18n.getCustomFeatureLocale('material_instructions_link')
		: undefined;

	private readonly isNewFileInfraEnabled = this.persist.getFlag('enableNewFileInfra');

	constructor(
		private readonly i18n: I18nService,
		private readonly store: Store<RootReducer.State>,
		private readonly productEditor: ProductEditorService,
		private readonly persist: StorePersistService,
	) {}

	/** Get the current step data from the store. */
	ngOnInit(): void {
		this.store
			.select(getStepData(this.productEditor.itemId, ProductFlowPage.Upload))
			.pipe(untilDestroyed(this))
			.subscribe(({ printfile } = {}) => {
				this.hasCartItem = !!this.productEditor.cartItem;
				const hasPrintFile = !!printfile;
				if (hasPrintFile) {
					// Restore printfile from step data when needed
					const currentPrintfile = this.productEditor.getPrintfile();
					if (!currentPrintfile || currentPrintfile.key !== printfile.key)
						this.productEditor.setPrintfile(printfile);
				}

				const currentPrintfile = this.productEditor.getPrintfile();
				this.setStateValid(!!currentPrintfile);

				if (currentPrintfile) {
					const { key, filename, expires_at } = currentPrintfile;
					this.currentFileItem = { key, filename, expires_at, status: FileUploadStatus.Success };
				}
			});
		this.item = this.productEditor.cartItem;
	}

	/** Collects the data from the current step. */
	collectStepData(): UploadMaterialData {
		return { printfile: this.productEditor.getPrintfile() };
	}

	/** Get the notification style class based on the current notification type. */
	getNotificationStyleClass(): {
		'notification-success': boolean;
		'notification-warning': boolean;
		'notification-danger': boolean;
	} {
		return {
			'notification-success': this.notification === UploadNotification.Success,
			'notification-warning': this.notification === UploadNotification.Warning,
			'notification-danger': this.notification === UploadNotification.Danger,
		};
	}

	/** Set component state to valid when the upload is successful. */
	onUploadSuccess(item: FileItem): void {
		this.notification = UploadNotification.Success;
		if (this.isNewFileInfraEnabled) {
			const { key, filename, expires_at } = item as FileItemV2;
			this.currentFileItem = { key, filename, expires_at, status: FileUploadStatus.Success };
		} else {
			const { key, filename } = item as FileItemLegacy;
			this.currentFileItem = { key, filename, status: FileUploadStatus.Success };
		}
		this.setStateValid(true);
	}

	/** Set component state to invalid when the file type is not supported. */
	onFiletypeError(): void {
		this.isFiletypeError = true;
		this.notification = UploadNotification.Danger;
		this.item = undefined; // Clear old item to prevent showing old preflight report.
		this.setStateValid(false);
	}

	/** Set the component state when the upload starts. */
	onUploadStart(item: FileItem): void {
		this.isFiletypeError = false;
		this.notification = undefined;
		this.item = undefined; // Clear old item to prevent showing old preflight report.
		if (this.isNewFileInfraEnabled) {
			const { key, filename, expires_at } = item as FileItemV2;
			this.currentFileItem = { key, expires_at, filename, status: FileUploadStatus.Uploading };
			this.productEditor.setPrintfile({ key, filename, expires_at, status: 'unprocessed' });
		} else {
			const { key, filename } = item as FileItemLegacy;
			this.currentFileItem = { key, filename, status: FileUploadStatus.Uploading };
			this.productEditor.setPrintfile({ key, filename });
		}
		this.setStateValid(true); // Allow adding to cart while upload is still in progress.
	}

	/** Reset the component state. */
	resetUploadSettings(): void {
		this.notification = undefined;
		this.productEditor.setPrintfile(undefined);
		this.item = undefined; // Clear old item to prevent showing old preflight report.
		this.setStateValid(false);
	}

	/** Set the product flow step state to valid or invalid. */
	private setStateValid(isValid: boolean): void {
		this.debug('isStateValid', isValid);
		this.store.dispatch(setStepStateValid(isValid));
	}
}
